import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Send, Mail, Phone, MapPin } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api, formatApiError } from "@/lib/api";
import { PHONE_DISPLAY } from "@/lib/brand";

const INTERESTS = ["AV Hardware (Media Group)", "AI Marketing (OTBMG)", "Industry Vertical Solution", "Enterprise Partnership", "Other"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", interest: INTERESTS[0], message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message || "Message sent!");
      setDone(true);
      setForm({ name: "", email: "", company: "", phone: "", interest: INTERESTS[0], message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white min-h-screen" data-testid="contact-page">
      <Navbar />
      <main className="pt-32 pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-16 lg:grid-cols-2">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <p className="text-xs sm:text-sm uppercase tracking-[0.2em] font-semibold text-ember">Get in Touch</p>
              <h1 className="mt-4 font-heading font-extrabold tracking-tight text-ink text-4xl sm:text-5xl">
                Let's build your growth environment.
              </h1>
              <p className="mt-5 text-lg text-slatey leading-relaxed max-w-lg">
                Tell us about your goals. Our team pairs physical AV infrastructure with autonomous Agentic AI marketing
                to deliver measurable, outcome-based results.
              </p>

              <div className="mt-10 space-y-5">
                {[
                  { icon: Mail, label: "Email", val: "growth@onthebrink.com" },
                  { icon: Phone, label: "Phone", val: PHONE_DISPLAY },
                  { icon: MapPin, label: "HQ", val: "United States — Serving enterprise & SMB nationwide" },
                ].map((c) => (
                  <div key={c.label} className="flex items-center gap-4">
                    <span className="flex h-11 w-11 items-center justify-center border border-slate-200 text-ember"><c.icon className="h-5 w-5" /></span>
                    <div><div className="text-xs uppercase tracking-wider text-slatey">{c.label}</div><div className="font-medium text-ink">{c.val}</div></div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}>
              <form onSubmit={submit} className="border border-slate-200 bg-white p-8 md:p-10" data-testid="contact-form">
                {done && (
                  <div className="mb-6 border border-ember/30 bg-ember/5 px-4 py-3 text-sm text-ember-dark" data-testid="contact-success">
                    Thanks! Our growth team will reach out shortly.
                  </div>
                )}
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Full Name *"><input required value={form.name} onChange={update("name")} data-testid="contact-name" className="otb-input" placeholder="Jane Doe" /></Field>
                  <Field label="Email *"><input required type="email" value={form.email} onChange={update("email")} data-testid="contact-email" className="otb-input" placeholder="jane@company.com" /></Field>
                  <Field label="Company"><input value={form.company} onChange={update("company")} data-testid="contact-company" className="otb-input" placeholder="Company Inc." /></Field>
                  <Field label="Phone"><input value={form.phone} onChange={update("phone")} data-testid="contact-phone" className="otb-input" placeholder="+1 ..." /></Field>
                </div>
                <div className="mt-5">
                  <Field label="I'm interested in">
                    <select value={form.interest} onChange={update("interest")} data-testid="contact-interest" className="otb-input">
                      {INTERESTS.map((o) => <option key={o}>{o}</option>)}
                    </select>
                  </Field>
                </div>
                <div className="mt-5">
                  <Field label="Message *">
                    <textarea required rows={4} value={form.message} onChange={update("message")} data-testid="contact-message" className="otb-input resize-none" placeholder="Tell us about your goals..." />
                  </Field>
                </div>
                <button type="submit" disabled={loading} data-testid="contact-submit"
                        className="mt-7 w-full inline-flex items-center justify-center gap-2 bg-ember text-white px-6 py-4 text-sm font-semibold hover:bg-ember-dark transition-colors disabled:opacity-60">
                  {loading ? "Sending..." : <>Send Message <Send className="h-4 w-4" /></>}
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </main>
      <Footer />
      <style>{`.otb-input{width:100%;border:1px solid #E2E8F0;background:#fff;padding:0.75rem 0.875rem;font-size:0.9rem;color:#0F172A;outline:none;transition:border-color .15s}.otb-input:focus{border-color:#EA580C}`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-wider font-semibold text-slatey mb-2">{label}</span>
      {children}
    </label>
  );
}
